"use client";

import Link from "next/link";
import { useEffect, useState } from "react";

/** "none" = aligned with the status items but deliberately unmarked — for
 *  entries that aren't fill-tracked (intro, closing, FAQ). */
type TocStatus = "empty" | "partial" | "complete" | "none";

interface TocItem {
  id: string;
  label: string;
  status?: TocStatus;
}

interface ArticleTocProps {
  items: TocItem[];
}

export default function ArticleTOC({ items }: ArticleTocProps) {
  const [activeId, setActiveId] = useState(items[0]?.id ?? "");

  useEffect(() => {
    if (!items.length) {
      setActiveId("");
      return;
    }

    const updateActiveFromViewport = () => {
      const topOffset = 140;
      let currentId = items[0].id;

      for (const item of items) {
        const section = document.getElementById(item.id);
        if (!section) continue;

        const sectionTop = section.getBoundingClientRect().top;
        if (sectionTop - topOffset <= 0) {
          currentId = item.id;
        } else {
          break;
        }
      }

      setActiveId(currentId);
    };

    const updateActiveFromHash = () => {
      const hashId = window.location.hash.replace("#", "");
      const isKnownHash = items.some((item) => item.id === hashId);
      if (isKnownHash) {
        setActiveId(hashId);
        return;
      }
      updateActiveFromViewport();
    };

    updateActiveFromHash();
    window.addEventListener("scroll", updateActiveFromViewport, { passive: true });
    window.addEventListener("hashchange", updateActiveFromHash);

    return () => {
      window.removeEventListener("scroll", updateActiveFromViewport);
      window.removeEventListener("hashchange", updateActiveFromHash);
    };
  }, [items]);

  return (
    <aside className="toc" aria-label="Inhaltsverzeichnis">
      <h5>Inhalt</h5>
      <ol>
        {items.map((item) => (
          <li key={item.id}>
            <Link
              href={`#${item.id}`}
              className={[item.id === activeId ? "toc-active" : "", item.status ? "toc-link-status" : ""]
                .filter(Boolean)
                .join(" ") || undefined}
              aria-current={item.id === activeId ? "location" : undefined}
            >
              {item.status ? (
                <span
                  className={`toc-status-dot toc-status-${item.status}`}
                  aria-hidden="true"
                  title={
                    item.status === "complete"
                      ? "Vollständig ausgefüllt"
                      : item.status === "partial"
                        ? "Teilweise ausgefüllt"
                        : item.status === "empty"
                          ? "Noch nicht ausgefüllt"
                          : undefined
                  }
                />
              ) : null}
              {item.label}
            </Link>
          </li>
        ))}
      </ol>
    </aside>
  );
}
